import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import UploadIcon from "@redotech/redo-icons/upload-01_filled.svg";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import {
  EvaluationTime,
  ProductTagMatchType,
} from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type { ProductTagState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { EvaluationTimeRadio } from "./evaluation-time-radio";
import { UploadCsvModal } from "./upload-csv-modal";

function matchTypeOptions(
  t: (key: string) => string,
): RadioGroupOption<ProductTagMatchType>[] {
  return [
    { id: ProductTagMatchType.ALL, label: t("All products") },
    { id: ProductTagMatchType.ANY, label: t("Any product") },
    { id: ProductTagMatchType.NONE, label: t("No product") },
  ];
}

export const ProductTagDetails = memo(function ProductTagDetails({
  state,
  setState,
  disabled,
  flowType,
}: ConditionTypeDetailsProps<ProductTagState>) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const stateRef = useRef(state);
  const setStateRef = useRef(setState);

  // Keep refs updated
  useEffect(() => {
    stateRef.current = state;
    setStateRef.current = setState;
  });

  const handleOpenUploadModal = useHandler(() => {
    setUploadModalOpen(true);
  });

  const handleCloseUploadModal = useHandler(() => {
    setUploadModalOpen(false);
  });

  const handleValuesUploaded = useHandler((newTags: string[]) => {
    const currentState = stateRef.current;
    const existingTags = currentState.tags || [];
    const uniqueTags = [...new Set([...existingTags, ...newTags])];
    setStateRef.current({ ...currentState, tags: uniqueTags });
  });

  const setTags = useHandler((tags: string[]) => {
    setState({ ...stateRef.current, tags });
  });

  const tags = useMemo(
    () => state.tags.map((tag) => ({ text: tag, color: "gray" as const })),
    [state.tags],
  );

  const isFinalizeFlow =
    flowType === FlowType.FINALIZE_RETURN ||
    flowType === FlowType.FINALIZE_CLAIM;

  const description = isFinalizeFlow
    ? t("Tags to search for in selected items")
    : undefined;

  return (
    <>
      {isFinalizeFlow && (
        <RedoRadioGroup
          disabled={disabled}
          options={matchTypeOptions(t)}
          setValue={(matchType) => setState({ ...state, matchType })}
          size="xs"
          value={state.matchType}
        />
      )}
      <RedoTagInputField
        delimiters={["newline"]}
        description={description}
        inputValue={inputValue}
        label={t("Product tags")}
        placeholder={t("Type and press Enter to add")}
        setInputValue={setInputValue}
        setTags={setTags}
        size="sm"
        state={disabled ? "disabled" : "default"}
        tags={tags}
      />
      <EvaluationTimeRadio
        defaultValue={EvaluationTime.TIME_OF_ORDER}
        disabled={disabled}
        label={t("Evaluate product tags at")}
        orderTimeTooltip={t(
          "Orders placed before you installed Redo don't have product tags saved, so this check won't match them. Use Time of return to check current tags.",
        )}
        setValue={(evaluationTime) => setState({ ...state, evaluationTime })}
        value={state.evaluationTime}
      />
      <Flex dir="column" gap="sm">
        <RedoButton
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={UploadIcon}
          onClick={handleOpenUploadModal}
          size="sm"
          type="button"
        >
          {t("Upload CSV")}
        </RedoButton>
      </Flex>
      {uploadModalOpen && (
        <UploadCsvModal
          onClose={handleCloseUploadModal}
          onValuesUploaded={handleValuesUploaded}
          open={uploadModalOpen}
          possibleColumnNames={["product_tag", "tag", "product_tags", "tags"]}
          valueLabel={t("product tags")}
        />
      )}
    </>
  );
});
